import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  BarChart3,
  ShoppingCart,
  Users,
  Shield,
  TrendingUp,
  FileText,
  Layers,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  Menu,
  X,
  Wallet,
  ClipboardList,
  UserCheck,
} from "lucide-react";

const FEATURES = [
  {
    icon: Layers,
    title: "Gestión de Proyectos",
    description:
      "Seguí el avance de cada obra en tiempo real. Asigná recursos, controlá hitos y tomá decisiones con datos precisos.",
  },
  {
    icon: BarChart3,
    title: "Contabilidad & Finanzas",
    description:
      "Cajas, flujo de fondos y cuentas corrientes centralizadas. Reportes financieros claros para mantener la salud de tu empresa.",
  },
  {
    icon: ShoppingCart,
    title: "Compras & Proveedores",
    description:
      "Creá órdenes de compra, gestioná proveedores y llevá cuentas corrientes con cada uno. Todo en un solo flujo.",
  },
  {
    icon: TrendingUp,
    title: "Módulo Comercial",
    description:
      "Listas de precio, clientes, ventas de unidades funcionales e historial de operaciones. Cierra más ventas con menos fricción.",
  },
  {
    icon: Wallet,
    title: "Cobranzas & Pagos",
    description:
      "Registrá pagos de clientes, generá comprobantes y mantené las cuentas corrientes siempre al día.",
  },
  {
    icon: Shield,
    title: "Roles & Permisos",
    description:
      "Cada usuario accede solo a lo que necesita. Configurá roles granulares para proteger la información sensible.",
  },
];

const MODULES = [
  {
    icon: ClipboardList,
    label: "Proyectos",
    color: "from-indigo-500 to-indigo-600",
  },
  {
    icon: FileText,
    label: "Órdenes de Compra",
    color: "from-violet-500 to-violet-600",
  },
  {
    icon: TrendingUp,
    label: "Ventas",
    color: "from-blue-500 to-blue-600",
  },
  {
    icon: Wallet,
    label: "Cashflow",
    color: "from-sky-500 to-sky-600",
  },
  {
    icon: Users,
    label: "Clientes",
    color: "from-purple-500 to-purple-600",
  },
  {
    icon: UserCheck,
    label: "Usuarios & Roles",
    color: "from-slate-500 to-slate-600",
  },
  {
    icon: BarChart3,
    label: "Contabilidad",
    color: "from-indigo-600 to-purple-600",
  },
  {
    icon: Building2,
    label: "Unidades Funcionales",
    color: "from-blue-600 to-indigo-600",
  },
];

const BENEFITS = [
  "Sin planillas de Excel desactualizadas",
  "Información unificada para todo el equipo",
  "Control de acceso por roles y permisos",
  "Cuentas corrientes de clientes y proveedores",
  "Historial completo de transacciones",
  "Multi-empresa: cada constructora aislada",
];

export default function LandingPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      {/* ── Navbar ─────────────────────────────────────────────────────────── */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-950/90 backdrop-blur-md border-b border-white/5 shadow-xl shadow-black/20"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/NewLogo.ico" alt="PruneStone" className="w-9 h-9 rounded-xl object-contain" />
            <span className="text-lg font-bold tracking-tight">PruneStone</span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-400">
            <a href="#features" className="hover:text-white transition-colors">
              Funcionalidades
            </a>
            <a href="#modules" className="hover:text-white transition-colors">
              Módulos
            </a>
            <a href="#benefits" className="hover:text-white transition-colors">
              Beneficios
            </a>
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/login"
              className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-all duration-200 shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40"
            >
              Iniciar Sesión
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-colors"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menú"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden bg-slate-950/95 backdrop-blur-md border-t border-white/5 px-6 py-4 space-y-3">
            <a
              href="#features"
              className="block py-2 text-sm text-slate-400 hover:text-white transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              Funcionalidades
            </a>
            <a
              href="#modules"
              className="block py-2 text-sm text-slate-400 hover:text-white transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              Módulos
            </a>
            <a
              href="#benefits"
              className="block py-2 text-sm text-slate-400 hover:text-white transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              Beneficios
            </a>
            <Link
              to="/login"
              className="flex items-center justify-center w-full py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-colors"
              onClick={() => setMenuOpen(false)}
            >
              Iniciar Sesión
            </Link>
          </div>
        )}
      </header>

      {/* ── Hero ───────────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-950" />

        {/* Glow blobs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-indigo-600/20 blur-[120px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-violet-600/15 blur-[100px] pointer-events-none" />

        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />

        {/* Floating shapes */}
        <div className="absolute top-32 right-20 w-20 h-20 border border-indigo-500/20 rounded-2xl rotate-12 animate-pulse hidden lg:block" />
        <div className="absolute bottom-40 left-16 w-14 h-14 border border-indigo-400/15 rounded-xl -rotate-12 hidden lg:block" />
        <div className="absolute top-1/2 right-1/3 w-10 h-10 rotate-45 rounded-lg bg-indigo-500/10 hidden lg:block" />

        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-sm font-medium mb-8">
            <Building2 className="w-4 h-4" />
            Software de Gestión para Constructoras
          </div>

          <h1 className="text-5xl sm:text-6xl xl:text-7xl font-bold leading-tight tracking-tight mb-6">
            Todo tu negocio
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 via-violet-400 to-indigo-300">
              en un solo lugar
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-400 leading-relaxed mb-10">
            PruneStone unifica la gestión de proyectos, finanzas, compras y ventas de tu constructora.
            Tu equipo trabaja sobre la misma información, en tiempo real.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-violet-600 rounded-2xl transition-all duration-300 shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 hover:-translate-y-0.5"
            >
              Acceder al sistema
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#features"
              className="flex items-center gap-2 px-8 py-4 text-base font-medium text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 rounded-2xl transition-all duration-200"
            >
              Ver funcionalidades
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>

          {/* Modules pills */}
          <div className="mt-16 flex flex-wrap justify-center gap-3">
            {["Proyectos", "Finanzas", "Compras", "Ventas", "Cobranzas", "Roles"].map((m) => (
              <span
                key={m}
                className="px-4 py-1.5 text-sm font-medium text-indigo-300 border border-indigo-500/20 bg-indigo-500/10 rounded-full backdrop-blur-sm"
              >
                {m}
              </span>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-slate-600">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-indigo-500/40" />
          <div className="w-1.5 h-1.5 rounded-full bg-indigo-500/40 animate-bounce" />
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────────── */}
      <section id="features" className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-indigo-400 text-sm font-semibold uppercase tracking-widest mb-3">
              Funcionalidades
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Todo lo que necesita una constructora
            </h2>
            <p className="max-w-2xl mx-auto text-slate-400 text-lg">
              Cada módulo está diseñado para el flujo real de trabajo de una empresa constructora.
              Nada que sobre, nada que falte.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="group relative p-6 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-indigo-500/20 transition-all duration-300"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-indigo-500/10 border border-indigo-500/20 mb-4 group-hover:bg-indigo-500/15 transition-colors">
                  <Icon className="w-5 h-5 text-indigo-400" />
                </div>
                <h3 className="text-base font-semibold text-white mb-2">{title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Modules grid ───────────────────────────────────────────────────── */}
      <section id="modules" className="py-24 bg-gradient-to-b from-slate-950 to-indigo-950/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-indigo-400 text-sm font-semibold uppercase tracking-widest mb-3">
                Módulos
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
                Un sistema modular
                <br />
                que crece con vos
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-8">
                Empezá por los módulos que más necesitás y habilitá los demás a medida que tu
                equipo crece. Todo conectado, todo en tiempo real.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl transition-all duration-200 shadow-lg shadow-indigo-500/25"
              >
                Ingresar al sistema
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-3">
              {MODULES.map(({ icon: Icon, label, color }) => (
                <div
                  key={label}
                  className="flex flex-col items-center gap-3 p-4 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:border-white/10 hover:bg-white/[0.05] transition-all duration-200 text-center group"
                >
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${color} opacity-80 group-hover:opacity-100 transition-opacity`}
                  >
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs font-medium text-slate-300 leading-tight">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Benefits ───────────────────────────────────────────────────────── */}
      <section id="benefits" className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Checklist */}
            <div className="order-2 lg:order-1 space-y-4">
              {BENEFITS.map((benefit) => (
                <div key={benefit} className="flex items-center gap-3">
                  <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-indigo-500/15 border border-indigo-500/25">
                    <CheckCircle2 className="w-3.5 h-3.5 text-indigo-400" />
                  </div>
                  <span className="text-slate-300 text-sm">{benefit}</span>
                </div>
              ))}
            </div>

            <div className="order-1 lg:order-2">
              <p className="text-indigo-400 text-sm font-semibold uppercase tracking-widest mb-3">
                Beneficios
              </p>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 leading-tight">
                Menos caos,
                <br />
                más control
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed">
                Dejá de trabajar con múltiples planillas y herramientas desconectadas.
                PruneStone centraliza la operación de tu constructora para que puedas enfocarte
                en lo que importa: construir.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────────────────────── */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-indigo-900 via-indigo-800 to-violet-900 p-1">
            {/* Inner glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-transparent" />
            <div className="relative rounded-[calc(1.5rem-1px)] bg-gradient-to-br from-indigo-950/80 to-slate-950/80 backdrop-blur-sm px-8 py-16 text-center">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl mx-auto mb-6">
                <img src="/NewLogo.ico" alt="PruneStone" className="w-14 h-14 rounded-2xl object-contain" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                ¿Listo para ordenar tu constructora?
              </h2>
              <p className="max-w-xl mx-auto text-slate-400 text-lg mb-8 leading-relaxed">
                Accedé al sistema y empezá a gestionar proyectos, finanzas y equipo
                desde un solo lugar.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-violet-600 rounded-2xl transition-all duration-300 shadow-xl shadow-indigo-500/30 hover:shadow-2xl hover:shadow-indigo-500/40 hover:-translate-y-0.5"
                >
                  Iniciar Sesión
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/register"
                  className="flex items-center gap-2 px-8 py-4 text-base font-medium text-indigo-300 border border-indigo-500/30 hover:border-indigo-500/50 hover:bg-indigo-500/10 rounded-2xl transition-all duration-200"
                >
                  Crear cuenta nueva
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/5 py-10">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/NewLogo.ico" alt="PruneStone" className="w-8 h-8 rounded-lg object-contain" />
            <span className="text-sm font-semibold text-white">PruneStone</span>
          </div>
          <p className="text-xs text-slate-600">
            &copy; {new Date().getFullYear()} PruneStone — Sistema de Gestión para Constructoras
          </p>
          <div className="flex items-center gap-6 text-xs text-slate-600">
            <Link to="/login" className="hover:text-slate-400 transition-colors">
              Iniciar Sesión
            </Link>
            <Link to="/register" className="hover:text-slate-400 transition-colors">
              Registrarse
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
